import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HotkeysHelpModalComponent } from '../components/_modals/hotkeys-help-modal/hotkeys-help-modal.component';
import { JumpToModalComponent } from '../components/_modals/jump-to-modal/jump-to-modal.component';
import { SubPageType } from '../enums/page-type.enum';
import { ModalService } from './modal.service';
import { NavigationService } from './navigation.service';

interface Options {
  element: HTMLElement | Document;
  keys: string;
}

@Injectable({ providedIn: 'root' })
export class HotKeysService implements OnDestroy {
  private defaults: Partial<Options> = {
    element: this.document,
  };

  private destroy$ = new Subject<void>();

  constructor(
    private eventManager: EventManager,
    @Inject(DOCUMENT) private document: Document,
    private modalService: ModalService,
    private navigationService: NavigationService,
  ) {
    this.initShortcuts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initShortcuts(): void {
    // Show hotkeys help modal
    this.addShortcut({ keys: 'shift.?' })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.navigationService.isOnSubPage(SubPageType.ProcessMap) || this.navigationService.isOnSubPage(SubPageType.VariantAnalysis)) {
          this.modalService.open(HotkeysHelpModalComponent);
        }
      });

    // Jump to modal
    this.addShortcut({ keys: 'control.x' })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.navigationService.isOnSubPage(SubPageType.ProcessMap) || this.navigationService.isOnSubPage(SubPageType.VariantAnalysis)) {
          this.modalService.open(JumpToModalComponent);
        }
      });
  }

  addShortcut(options: Partial<Options>): Observable<Event> {
    const merged = { ...this.defaults, ...options };
    const event = `keydown.${merged.keys}`;

    return new Observable<Event>(observer => {
      const handler = (e: Event): void => {
        // Don't handle shortcuts when a modal is open
        if (this.modalService.isSomeOpen()) {
          return;
        }

        // Only prevent default for non-input elements to avoid interfering with typing
        const target = e.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

        if (!isInput) {
          e.preventDefault();
          e.stopPropagation();
        }

        observer.next(e);
      };

      const dispose = this.eventManager.addEventListener(merged.element as HTMLElement, event, handler);

      return () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        dispose();
      };
    });
  }
}
